import { applyQueryConfig, ServiceHandler } from '../../utils';

import { type PageableResponse, standardQuerySerializer } from '../serializers';
import {
  SUBMISSIONS_QUERY_PARAM_MAPPING,
  withTeamScope,
} from './submissions.param-mapping';

import type { SelectOption } from '../../console/agencies/quotes/PrimeX/customizeQuote/reducer/types';
import type { FieldValue } from '../../form-engine/types';
import type { P100SecurityQuestions } from '../../types';
import type {
  CreateSubmissionPayload,
  DeclineSubmissionPayload,
  ISubmissionMetricsResponse,
  SubmissionDetailsDto,
  SubmissionEligibleProductsResponse,
  SubmissionMergedQuestionsDto,
  SubmissionFirmographics,
  SubmissionsDetailsResponseDto,
  SubmissionStatus,
  SubmissionClaimsStatusDto,
  SubmissionTableRow,
  UpdateAgencyDto,
} from './submissions.api.types';

export const searchSubmissions = ServiceHandler<
  unknown,
  PageableResponse<SubmissionTableRow>
>({
  url: '/api/account/v1/submission/search',
  method: 'GET',
  paramsSerializer: (params) => {
    const processedParams = withTeamScope(
      applyQueryConfig(SUBMISSIONS_QUERY_PARAM_MAPPING, params)
    );
    const serializedParams = standardQuerySerializer(processedParams);
    return serializedParams;
  },
});

export const searchRenewalSubmissions = ServiceHandler<
  unknown,
  PageableResponse<SubmissionTableRow>
>({
  url: '/api/account/v1/submission/search/renewals',
  method: 'GET',
  paramsSerializer: (params) => {
    const processedParams = applyQueryConfig(
      SUBMISSIONS_QUERY_PARAM_MAPPING,
      params
    );

    const serializedParams = standardQuerySerializer(processedParams);
    return serializedParams;
  },
});

export const fetchSubmissionsMetrics = ServiceHandler<
  unknown,
  ISubmissionMetricsResponse
>({
  url: '/api/account/v1/submission/stats',
  method: 'GET',
});

/**
 * Utilized to create submission records within our platform.
 * Creates an account if one does not exist with the passed firmographic data.
 * Implicitly utilizes agency information from the user's JWT when that data is not passed within the request body.
 *
 * @date 2/26/2024 - 1:02:33 PM
 *
 * @type {*}
 */
export const createSubmission = ServiceHandler<
  CreateSubmissionPayload,
  SubmissionDetailsDto
>({
  url: '/api/account/v1/submission',
  method: 'POST',
});

export const fetchSubmissionsStatuses = ServiceHandler<
  unknown,
  SelectOption<string>[]
>({
  url: '/api/account/v1/submission/options/submission-status',
});

export const bulkAssignAssignee = ServiceHandler({
  url: '/api/account/v1/submission/bulk-assign',
  method: 'PUT',
});

type GetSubmissionsUsersResponse = {
  firstName: string;
  lastName: string;
  id: string;
  email?: string | null;
}[];

export const getSubmissionsUsers = ServiceHandler<
  unknown,
  GetSubmissionsUsersResponse
>({
  url: '/api/auth/v1/users/submissions',
});

export type UpdateSubmissionDto = {
  assignee?: string;
  needBy?: string;
  automation?: boolean;
  firmographics?: Partial<SubmissionFirmographics>;
  dynamicFields?: Record<string, FieldValue>;
  agencyAccount?: {
    accountId?: string;
    agencyId?: string;
    agencyName?: string;
    agent?: {
      firstName?: string;
      lastName?: string;
      emailAddress?: string;
      phoneNumber?: string;
      teams?: Array<{
        teamId: string;
        teamName: string;
      }>;
    };
  };
  correspondenceEmail?: string;
  confidenceScores?: Record<string, number>;
};

export const updateSubmission = ServiceHandler<
  UpdateSubmissionDto,
  SubmissionDetailsDto
>({
  url: '/api/account/v1/submission/:submissionId',
  method: 'PUT',
});

export const getSubmissionById = ServiceHandler<unknown, SubmissionDetailsDto>({
  url: '/api/account/v1/submission/:id',
  method: 'GET',
});

export interface GenerateReplyResponse {
  subject: string;
  bodyText: string;
  bodyHtml: string;
  originalSubmissionSummary: {
    insuredName: string;
    brokerName: string;
    submittedOn: string;
    submissionNumber: string;
  };
  coverageOutcomes: Array<{
    productLine: string;
    outcomeType: string;
    summary: string;
    quoteDetails?: {
      quoteId: string;
      accountId: string;
      quoteNumber: string;
      productType: string;
      premium: number;
      expirationDate: string;
      quoteStatus: string;
      daysUntilExpiration: number;
      validQuote: boolean;
      formattedPremium: string;
    };
  }>;
  llmMetadata: {
    requestId: string;
    roleUsed: string;
    model: string;
    tokensUsed: number;
    responseTimeMs: number;
    temperature: number;
    guardrailsApplied: boolean;
    maxTokens: number;
  };
  warnings: string[];
  success: boolean;
}

export const generateReply = ServiceHandler<unknown, GenerateReplyResponse>({
  url: '/api/account/v1/submission/generate-reply/:submissionId',
  method: 'POST',
});

export const getSubmissionWithConfidenceById = ServiceHandler<
  unknown,
  SubmissionsDetailsResponseDto
>({
  url: '/api/account/v1/submission/:id/confidence-details',
  method: 'GET',
});

export const getSubmissionByMsgId = ServiceHandler({
  url: '/api/account/v1/submission/msgId/:msgId',
  paramsSerializer: (params) => {
    // Extract just the UUID part if there's an email domain appended
    const msgId = params.msgId?.split('@')[0] || params.msgId;
    return standardQuerySerializer({ msgId });
  },
});

/** The submission's underwriting questions merged into one form across its coverage options' products. */
export const getSubmissionMergedQuestions = ServiceHandler<
  unknown,
  SubmissionMergedQuestionsDto
>({
  url: '/api/account/v1/submission/:id/questions',
});

export const getSubmissionEligibleProducts = ServiceHandler<
  unknown,
  SubmissionEligibleProductsResponse
>({
  url: '/api/account/v1/submission/eligible-products/:id',
});

export const certifySubmission = ServiceHandler({
  url: '/api/account/v1/submission/:id/certify',
  method: 'POST',
});

export const declineSubmission = ServiceHandler<
  DeclineSubmissionPayload,
  unknown
>({
  url: '/api/account/v1/submission/decline/:id',
  method: 'PUT',
});

export type ReopenSubmissionPayload = {
  reOpenReason: string;
  reOpenReasonDescription: string;
};

/** Re-opens a DECLINED or LOST submission. */
export const reopenSubmission = ServiceHandler<
  ReopenSubmissionPayload,
  unknown
>({
  url: '/api/account/v1/submission/reopen/:id',
  method: 'PUT',
});

export type P250SecurityAnswers = {
  claimHistory: number;
  isSecurityOfficer: boolean;
  isSecurityTraining: boolean;
  p250Details: {
    backupFrequency: string;
    civilOrCriminalAction: boolean;
    defaultAggLimit: number;
    lossInBusinessIncome: boolean;
    pastCyberIncident: boolean;
    patchingFrequency: string;
    pendingLitigation: boolean;
    referralReason?: string;
    securityBreachRequiresNotification: boolean;
    uwReferral: boolean;
  };
  securityAnswersDto: {
    backupFrequency: string;
    patchingFrequency: string;
    dmzSeparation?: boolean;
    isAuthenticatingFundTransferRequest?: boolean;
    isPreventingUnauthorizedWireTransfers?: boolean;
    isVerifyingBankAccount?: boolean;
    mfaCloudDeployments?: boolean;
    mfaEmail?: boolean;
    mfaRemoteAccess?: boolean;
    testedFullFailover?: boolean;
    thirdPartySecurityAgreement?: boolean;
  };
  useCloudStorage: boolean;
  useEncryption: boolean;
};

export type LegacySecurityQuestionsDtoResponse = {
  p100SecurityAnswers: P100SecurityQuestions;
  p250SecurityAnswers: P250SecurityAnswers;
};

export const getDynamicFormsLegacyAdapter = ServiceHandler<
  unknown,
  LegacySecurityQuestionsDtoResponse
>({
  url: '/api/account/v1/questions/adapt/:productType',
  method: 'POST',
});

export const bulkCreateP250Quotes = ServiceHandler({
  url: '/api/quote/v1/P250/bulk',
  method: 'POST',
});

export const createP250QuoteForSubmission = ServiceHandler({
  url: '/api/quote/v1/P250/submission/:submissionId',
  method: 'POST',
});

export const getActiveSubmissionsForAccount = ServiceHandler<
  unknown,
  PageableResponse<SubmissionTableRow>
>({
  url: '/api/account/v1/submission/active/:accountId',
  method: 'GET',
});

export const updateAssigneeOnSubmission = ServiceHandler({
  url: '/api/account/v1/submission/update-assignee/:submissionId',
  method: 'PUT',
});

/**
 * FIR-20818 — re-runs the assignment service for this submission against
 * current rules + appointer state and persists the result. Returns the
 * refreshed SubmissionDetailsDto so the caller can show the new assignee.
 */
export const recomputeSubmissionAssignments = ServiceHandler({
  url: '/api/account/v1/submission/:submissionId/recompute-assignments',
  method: 'POST',
});

export const getSubmissionsByAccountId = ServiceHandler<
  unknown,
  SubmissionDetailsDto[]
>({
  url: '/api/account/v1/submission/by-account/:accountId',
});

export const referSubmissionToCarrier = ServiceHandler({
  url: '/api/account/v1/submission/carrier-refer/:productType',
  method: 'PUT',
});

export const getLossRunsBySubmissionNumber = ServiceHandler({
  url: '/api/account/v1/submission/by-number/:submissionNumber/loss-runs',
  method: 'GET',
});

export const updateConfidenceScores = ServiceHandler<
  Record<string, number>,
  string
>({
  url: '/api/account/v1/submission/:submissionId/confidence-scores',
  method: 'PUT',
});

export const updateSubmissionAccount = ServiceHandler({
  url: '/api/account/v1/agencyAccount/:submissionId/account',
  method: 'PUT',
});

export const updateSubmissionAgency = ServiceHandler<UpdateAgencyDto, string>({
  url: '/api/account/v1/agencyAccount/:submissionId/agency',
  method: 'PUT',
});

export type MarkSubmissionAsLostPayload = {
  lostReason: string;
  lostReasonDescription: string;
};

export const markSubmissionAsLost = ServiceHandler<
  MarkSubmissionAsLostPayload,
  unknown
>({
  url: '/api/account/v1/submission/lost/:id',
  method: 'PUT',
});

export type MarkRenewalSubmissionAsLostPayload = {
  lostReason: string;
  lostReasonDescription?: string;
  lostReasonDetails?: { competitor?: string; notes?: string; premium?: number };
};

export const markRenewalSubmissionAsLost = ServiceHandler<
  MarkRenewalSubmissionAsLostPayload,
  unknown
>({
  url: '/api/account/v1/submission/lost/:id',
  method: 'PUT',
});

export type CarrierReferralResponse = {
  willReferToCarrier: boolean;
};

export const checkCarrierReferral = ServiceHandler<
  UpdateSubmissionDto,
  CarrierReferralResponse
>({
  url: '/api/account/v1/submission/:submissionNumber/carrier-referral',
  method: 'POST',
});

export type MarkSubmissionAsExcessPayload = {
  requestedLimit: number;
  attachmentPoint: number;
  quoteNumber: string;
  description: string;
};

export const markSubmissionAsExcess = ServiceHandler<
  MarkSubmissionAsExcessPayload,
  unknown
>({
  url: '/api/account/v1/submission/excess/:id',
  method: 'PUT',
});

export const createRenewalSubmission = ServiceHandler({
  url: '/api/account/v1/submission/:policyId/renewal-submission',
  method: 'POST',
});

export const runRenewalSubmissionSyncJob = ServiceHandler({
  url: '/api/account/v1/submission/trigger-renewal-submission-creation',
  method: 'POST',
});

export interface UpdateSubmissionAssignmentsPayload {
  aeEmail?: string;
  uwEmail?: string;
}

export const updateSubmissionAssignments = ServiceHandler<
  UpdateSubmissionAssignmentsPayload,
  unknown
>({
  url: '/api/account/v1/submission/:submissionId/team',
  method: 'PUT',
});

export const updateSubmissionNeedBy = ServiceHandler<
  { needBy: string | null },
  SubmissionDetailsDto
>({
  url: '/api/account/v1/submission/:submissionId/need-by',
  method: 'PUT',
});

export const runRenewalAssignmentsSyncJob = ServiceHandler({
  url: '/api/account/v1/submission/trigger-renewal-submission-assignment-update',
  method: 'POST',
});

export const runRenewalEligibilityRulesSyncJob = ServiceHandler({
  url: '/api/account/v1/submission/trigger-renewal-triage',
  method: 'POST',
});

export const runRenewalAutoQuotingSyncJob = ServiceHandler({
  url: '/api/account/v1/submission/trigger-renewal-auto-quoting',
  method: 'POST',
});

export const runRenewalSubmissionCleanupJob = ServiceHandler({
  url: '/api/account/v1/submission/trigger-renewal-submission-cleanup',
  method: 'POST',
});

export const runZsp76DayClaimsCheck = ServiceHandler({
  url: '/api/account/v1/submission/trigger-zsp-renewal-76-day-claims-triage',
  method: 'POST',
});

export const setSubmissionScheduled = ServiceHandler({
  url: '/api/account/v1/submission/:submissionId/scheduled',
  method: 'PUT',
});

export type SubmissionDecisionType =
  | 'REFERRAL'
  | 'NEEDS_APP'
  | 'NON_RENEWED'
  | 'BIND_ORDER_RECEIVED'
  | 'PENDING';

export type RecordSubmissionDecisionPayload = {
  type: SubmissionDecisionType;
  reasons?: string[];
};

export const recordSubmissionDecision = ServiceHandler<
  RecordSubmissionDecisionPayload,
  unknown
>({
  url: '/api/account/v1/submission/:submissionId/decisions',
  method: 'POST',
});

export type OverrideSubmissionStatusPayload = {
  submissionStatus: SubmissionStatus;
  reason: string;
};

/** Sets any status by hand. The reason is written to the submission's notes. */
export const overrideSubmissionStatus = ServiceHandler<
  OverrideSubmissionStatusPayload,
  unknown
>({
  url: '/api/account/v1/submission/:submissionId/status-override',
  method: 'PUT',
});

export type UpdateSubmissionExpiryDatePayload = {
  /** yyyy-MM-dd */
  expiryDate: string;
  reason?: string;
};

/** Corrects a renewal's policy expiration date. The status is re-derived from the new date. */
export const updateSubmissionExpiryDate = ServiceHandler<
  UpdateSubmissionExpiryDatePayload,
  SubmissionDetailsDto
>({
  url: '/api/account/v1/submission/:submissionId/expiry-date',
  method: 'PUT',
});

export const retriggerTriagingRules = ServiceHandler<
  unknown,
  SubmissionDetailsDto
>({
  url: '/api/account/v1/submission/:submissionId/retrigger-triaging-rules',
  method: 'POST',
});

export const autoQuoteSubmission = ServiceHandler({
  url: '/api/account/v1/submission/:submissionId/auto-quote',
  method: 'POST',
});

export const generateNewBusinessQuote = ServiceHandler({
  url: '/api/account/v1/submission/:submissionId/new-business-quote',
  method: 'POST',
});

export const getSubmissionClaimsStatus = ServiceHandler<
  unknown,
  SubmissionClaimsStatusDto
>({
  url: '/api/account/v1/submission/:id/claims',
  method: 'GET',
});

export interface RenewalQuoteSummary {
  id: string;
  product: string;
}

export const getRenewalQuotesForSubmission = ServiceHandler<
  unknown,
  RenewalQuoteSummary[]
>({
  url: '/api/account/v1/submission/:submissionId/renewal-quotes',
  method: 'GET',
});
